import { useContext, useEffect, useMemo, useRef } from 'react';
import type {
  _PiResolvedCommonViewFieldConfig,
  KeyPath,
} from '@piying/view-core';
import { CVA, InjectorToken, PI_VIEW_FIELD_TOKEN } from '../token';
import { useSignalToRef } from '../util/signal-convert';
import { PiyingWrapper } from './wrapper';
import {
  createViewControlLink,
  type ControlValueAccessor,
} from '@piying/view-core';
export interface PiyingFieldTemplateProps {
  field: _PiResolvedCommonViewFieldConfig;
  path?: KeyPath;
}

export function PiyingFieldTemplate(props: PiyingFieldTemplateProps) {
  const keyPath = props.path;
  const field = useMemo(() => {
    return keyPath ? props.field.get(keyPath)! : props.field;
  }, [props.field, keyPath]);

  const fieldInputs = useSignalToRef(field, (field) => ({
    ...field.attributes(),
    ...field.inputs(),
    ...field.outputs(),
  }));
  const renderConfig = useSignalToRef(field, (field) => field.renderConfig());
  const wrappers = useSignalToRef(field, (field) => field.wrappers());
  const control = field.form.control;
  const define = useSignalToRef(field, (field) => field.define?.());
  const ComponentType = define?.type;
  const isHidden = useMemo(() => {
    return !!renderConfig.hidden || !ComponentType;
  }, [renderConfig.hidden, ComponentType]);
  const injector = useContext(InjectorToken)!;

  const controlRef = useRef<ControlValueAccessor>(undefined);
  const fieldControlInput = useMemo(
    () => ({ ...fieldInputs, [CVA]: controlRef }),
    [fieldInputs, controlRef],
  );

  useEffect(() => {
    let dispose: (() => any) | undefined;
    if (controlRef.current) {
      dispose = createViewControlLink(
        (() => control) as any,
        controlRef.current,
        injector,
      );
    }
    return () => {
      dispose?.();
      dispose = undefined;
    };
  }, [controlRef.current, control, injector]);
  return (
    <>
      <PI_VIEW_FIELD_TOKEN value={field}>
        {!isHidden ? (
          <PiyingWrapper wrappers={wrappers}>
            {field.form.control ? (
              <ComponentType {...fieldControlInput}></ComponentType>
            ) : (
              <ComponentType {...fieldInputs}></ComponentType>
            )}
          </PiyingWrapper>
        ) : undefined}
      </PI_VIEW_FIELD_TOKEN>
    </>
  );
}
